import React from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import './SyncProgressBar.css';

const SyncProgressBar = ({
  isVisible,
  progress,
  message,
  status, // 'idle', 'initiating', 'waiting', 'running', 'syncing', 'completed', 'error', 'cancelled'
  boardName,
  waitInfo, // { elapsed, remaining, lockAge, lockTTL }
  onCancel,
  onRetry,
  onHide,
  showDetails = false,
  // WebSocket status props
  isWebSocketEnabled = false,
  webSocketConnectionState = null,
  onRetryWebSocketConnection = null
}) => {
  if (!isVisible) return null;

  const getVariant = () => {
    switch (status) {
      case 'waiting':
        return 'warning';
      case 'running':
      case 'syncing':
        return 'primary';
      case 'completed':
        return 'success';
      case 'error':
        return 'danger';
      case 'cancelled':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const getAlertVariant = () => {
    switch (status) {
      case 'waiting':
        return 'warning';
      case 'completed':
        return 'success';
      case 'error':
        return 'danger';
      case 'cancelled':
        return 'warning';
      default:
        return 'info';
    }
  };

  const isActive = status === 'running' || status === 'syncing';
  const showActions = status === 'error' || status === 'cancelled';

  return (
    <div className="sync-progress-container mb-3">
      <Alert variant={getAlertVariant()} className="mb-2">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <strong className="me-2">
              {status === 'waiting' && '⏳ Waiting: '}
              {(status === 'running' || status === 'syncing') && '🔄 Syncing: '}
              {status === 'completed' && '✅ Completed: '}
              {status === 'error' && '❌ Failed: '}
              {status === 'cancelled' && '⚠️ Cancelled: '}
              {boardName || 'Unknown Project'}
            </strong>
            {/* WebSocket Status Badge */}
            {isWebSocketEnabled && webSocketConnectionState && (
              <span className={`badge ${webSocketConnectionState.isConnected ? 'bg-success' : 'bg-warning'} ms-2`} 
                    style={{ fontSize: '10px' }}>
                {webSocketConnectionState.isConnected ? '🔗 Real-time' : '📡 Polling'}
              </span>
            )}
          </div>
          
          {/* Close button - show for all states except running */}
          {status !== 'running' && onHide && (
            <Button 
              variant="link" 
              size="sm"
              onClick={onHide}
              className="text-muted p-0 ms-2"
              style={{ fontSize: '20px', lineHeight: '1', textDecoration: 'none' }}
              title="Close"
            >
              ×
            </Button>
          )}
          
          {/* Cancel button - only show when running */}
          {status === 'running' && onCancel && (
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={onCancel}
              className="sync-cancel-btn"
            >
              Cancel
            </Button>
          )}
        </div>
        
        {/* Progress message */}
        {message && (
          <div className="sync-message mt-2">
            <small>{message}</small>
          </div>
        )}
        
        {/* Waiting Info */}
        {status === 'waiting' && waitInfo && (
          <div className="waiting-info mt-2">
            <small className="text-warning">
              ⏳ Another sync in progress. Your sync will start automatically when complete.
            </small>
            {waitInfo.remaining && (
              <small className="text-muted d-block mt-1">
                Estimated wait: {waitInfo.remaining}s
              </small>
            )}
          </div>
        )}
      </Alert>

      {/* Progress Bar */}
      <div className="progress-wrapper">
        <ProgressBar
          now={progress}
          variant={getVariant()}
          animated={isActive}
          striped={isActive}
          className="sync-progress-bar"
        />
        
        {/* Progress percentage - follows the progress bar */}
        <div 
          className="progress-percentage" 
          style={{ 
            left: progress > 0 ? `calc(${progress}% - 40px)` : '0',
            color:'#fff'
          }}
        >
          <small>{Math.round(progress)}%</small>
        </div>
      </div>

      {/* Action buttons for error/cancelled states */}
      {showActions && (
        <div className="sync-actions mt-2">
          {onRetry && (
            <Button 
              variant="primary" 
              size="sm" 
              onClick={onRetry}
              className="me-2"
            >
              Retry Sync
            </Button>
          )}
        </div>
      )}

      {/* Details section (expandable) */}
      {showDetails && (
        <div className="sync-details mt-2">
          <details>
            <summary className="text-muted" style={{ cursor: 'pointer', fontSize: '12px' }}>
              View Details
            </summary>
            <div className="mt-1 p-2 bg-light rounded" style={{ fontSize: '11px' }}>
              <div><strong>Status:</strong> {status}</div>
              <div><strong>Progress:</strong> {Math.round(progress)}%</div>
              <div><strong>Board:</strong> {boardName || 'N/A'}</div>
              {message && <div><strong>Message:</strong> {message}</div>}
              <div><strong>Updated:</strong> {new Date().toLocaleTimeString()}</div>
              
              {/* WebSocket Connection Details */}
              {isWebSocketEnabled && webSocketConnectionState && (
                <>
                  <hr className="my-1" />
                  <div><strong>Connection:</strong> {webSocketConnectionState.isConnected ? '🟢 WebSocket Connected' : '🟡 Using Polling Fallback'}</div>
                  {webSocketConnectionState.error && (
                    <div><strong>Error:</strong> <span className="text-danger">{webSocketConnectionState.error}</span></div>
                  )}
                  {webSocketConnectionState.reconnectAttempts > 0 && (
                    <div><strong>Reconnect Attempts:</strong> {webSocketConnectionState.reconnectAttempts}</div>
                  )}
                  {!webSocketConnectionState.isConnected && onRetryWebSocketConnection && (
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={onRetryWebSocketConnection}
                      className="mt-1"
                      style={{ fontSize: '10px', padding: '2px 6px' }}
                    >
                      Retry Connection
                    </Button>
                  )}
                </>
              )}
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default SyncProgressBar;